import React from "react"
import { twMerge } from "tailwind-merge"

import { getSectionColorScheme } from "../utils/colorModeUtils"
import { formatFontFamily, formatHeadingFontFamily, stripWysiwygInlineStylesFromHtml } from "../utils/fontUtils"
import { getThemeShapeClasses, applyButtonTheme, getButtonClasses } from "../utils/themeUtils"
import {
  buildServiceUrl,
  buildEventTypeUrl,
  buildPublicPageUrl,
  resolveBookableEventTypeSlugForCta
} from "../utils/urlUtils"
import { getWysiwygScopeId, getWysiwygScopeSelector } from "../utils/wysiwygScopeUtils"
import { DEFAULT_HIGHLIGHT_CLASSES } from "../WebsiteBuilderV2Page"

import { ScrollReveal } from "./shared/ScrollReveal"

const CTASectionDesktop = ({ section, practice, theme, highlightedField }) => {
  const isHighlighted = highlightedField === `section-${section.id}`
  const settings = section.settings || {}
  const layout = settings.layout || "content_centered"

  // CTA Button settings
  const showButton = settings.showButton || false
  const buttonText = settings.buttonText || "Book Now"
  const buttonAction = settings.buttonAction || "service"
  const buttonTarget = settings.buttonTarget || ""

  const handleCTAClick = () => {
    switch (buttonAction) {
      case "page":
        if (buttonTarget) {
          window.location.href = buildPublicPageUrl(buttonTarget, practice)
        }
        break
      case "service":
        if (buttonTarget) {
          const eventSlug = resolveBookableEventTypeSlugForCta(buttonTarget, practice)
          if (eventSlug) {
            window.location.href = buildEventTypeUrl(eventSlug, practice)
          } else {
            window.location.href = buildServiceUrl(buttonTarget, practice)
          }
        }
        break
      case "external":
        if (buttonTarget) {
          // Sanitize URL: ensure it starts with http:// or https://
          let sanitizedUrl = buttonTarget.trim()
          if (!sanitizedUrl.startsWith("http://") && !sanitizedUrl.startsWith("https://")) {
            sanitizedUrl = `https://${sanitizedUrl}`
          }
          // Additional validation: check if URL is valid
          try {
            new URL(sanitizedUrl)
            window.open(sanitizedUrl, "_blank", "noopener,noreferrer")
          } catch (e) {
            // Invalid URL - silently fail
          }
        }
        break
      default:
        break
    }
  }

  // Get color scheme based on color mode
  const colorScheme = getSectionColorScheme(settings, theme)

  // Use color scheme for colors
  const backgroundColor = colorScheme.sectionBackground
  const headingColor = colorScheme.headingColor
  const bodyColor = colorScheme.bodyColor
  const linkColor = colorScheme.linkColor
  const wysiwygScope = getWysiwygScopeSelector(section.id, "cta")
  const wysiwygScopeId = getWysiwygScopeId(section.id, "cta")

  // Get highlights from settings with fallback to legacy highlights column
  const highlights = settings.highlights || section.highlights || []

  const renderContent = (textAlign = "center") => {
    // Use color scheme for text colors
    const titleColor = headingColor
    const subheadlineColor = headingColor
    const descriptionColor = bodyColor

    return (
      <div className="space-y-4">
        {/* Headline and subheadline use textAlign */}
        <div className={`text-${textAlign}`}>
          {section.headline && (
            <h2
              className="cta-section-headline text-[40px] font-bold leading-[48px]"
              style={{
                fontFamily: formatHeadingFontFamily(theme?.fonts?.heading),
                fontWeight: theme?.fonts?.headingWeight || "700",
                color: titleColor
              }}>
              {section.headline}
            </h2>
          )}
          {section.subheadline && (
            <p
              className="cta-section-subheadline text-[27px] leading-[32px]"
              style={{
                fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                color: subheadlineColor
              }}>
              {section.subheadline}
            </p>
          )}
        </div>
        {/* Body content - let WYSIWYG classes control alignment */}
        {section.body && (
          <>
            <style>{`
              .website-builder-v2-public .cta-section-headline {
                font-family: ${formatHeadingFontFamily(theme?.fonts?.heading)} !important;
                font-weight: ${theme?.fonts?.headingWeight || "700"} !important;
              }
              .website-builder-v2-public .cta-section-subheadline {
                font-family: ${formatFontFamily(theme?.fonts?.paragraph)} !important;
              }
              ${wysiwygScope} {
                text-align: initial !important;
              }
              ${wysiwygScope} ul {
                list-style-type: disc !important;
                margin-left: 1.5rem !important;
                margin-bottom: 1rem !important;
              }
              ${wysiwygScope} ol {
                list-style-type: decimal !important;
                margin-left: 1.5rem !important;
                margin-bottom: 1rem !important;
              }
              ${wysiwygScope} li {
                margin-bottom: 0.5rem !important;
                display: list-item !important;
              }
              ${wysiwygScope} .ql-align-right {
                text-align: right !important;
              }
              ${wysiwygScope} .ql-align-center {
                text-align: center !important;
              }
              ${wysiwygScope} .ql-align-justify {
                text-align: justify !important;
              }
              ${wysiwygScope} .ql-align-left {
                text-align: left !important;
              }
              ${wysiwygScope} a {
                color: ${linkColor} !important;
                text-decoration: underline !important;
              }
              ${wysiwygScope} a:hover {
                opacity: 0.8 !important;
                text-decoration: underline !important;
              }
              ${wysiwygScope} p {
                margin-bottom: 1rem !important;
                margin-top: 0 !important;
              }
              ${wysiwygScope} p:first-child {
                margin-top: 0 !important;
              }
              ${wysiwygScope} p:last-child {
                margin-bottom: 0 !important;
              }
              ${wysiwygScope} strong,
              ${wysiwygScope} b {
                font-weight: ${theme?.fonts?.paragraphBoldWeight || "600"} !important;
                color: inherit !important;
              }
              ${wysiwygScope} em,
              ${wysiwygScope} i {
                font-style: italic !important;
              }
              ${wysiwygScope} u {
                text-decoration: underline !important;
              }
              .website-builder-v2-public ${wysiwygScope},
              .website-builder-v2-public ${wysiwygScope} p,
              .website-builder-v2-public ${wysiwygScope} li,
              .website-builder-v2-public ${wysiwygScope} span {
                font-family: ${formatFontFamily(theme?.fonts?.paragraph)} !important;
              }
              ${wysiwygScope} h1 {
                font-family: ${formatHeadingFontFamily(theme?.fonts?.heading)} !important;
                font-weight: ${theme?.fonts?.headingWeight || "700"} !important;
                font-size: 56px !important;
                line-height: 64px !important;
                margin-bottom: 1rem !important;
                margin-top: 1.5rem !important;
                color: ${descriptionColor} !important;
              }
              ${wysiwygScope} h2 {
                font-family: ${formatHeadingFontFamily(theme?.fonts?.heading)} !important;
                font-weight: ${theme?.fonts?.headingWeight || "700"} !important;
                font-size: 40px !important;
                line-height: 48px !important;
                margin-bottom: 1rem !important;
                margin-top: 1.5rem !important;
                color: ${descriptionColor} !important;
              }
              ${wysiwygScope} h3 {
                font-family: ${formatHeadingFontFamily(theme?.fonts?.heading)} !important;
                font-weight: ${theme?.fonts?.headingWeight || "700"} !important;
                font-size: 28px !important;
                line-height: 32px !important;
                margin-bottom: 1rem !important;
                margin-top: 1.5rem !important;
                color: ${descriptionColor} !important;
              }
              ${wysiwygScope} h4 {
                font-family: ${formatHeadingFontFamily(theme?.fonts?.heading)} !important;
                font-weight: ${theme?.fonts?.headingWeight || "700"} !important;
                font-size: 20px !important;
                line-height: 24px !important;
                margin-bottom: 0.75rem !important;
                margin-top: 1.25rem !important;
                color: ${descriptionColor} !important;
              }
              ${wysiwygScope} h5 {
                font-family: ${formatHeadingFontFamily(theme?.fonts?.heading)} !important;
                font-weight: ${theme?.fonts?.headingWeight || "700"} !important;
                font-size: 18px !important;
                line-height: 28px !important;
                margin-bottom: 0.5rem !important;
                margin-top: 1rem !important;
                color: ${descriptionColor} !important;
              }
              ${wysiwygScope} h6 {
                font-family: ${formatHeadingFontFamily(theme?.fonts?.heading)} !important;
                font-weight: ${theme?.fonts?.headingWeight || "700"} !important;
                font-size: 16px !important;
                line-height: 24px !important;
                margin-bottom: 0.5rem !important;
                margin-top: 1rem !important;
                color: ${descriptionColor} !important;
              }
              ${wysiwygScope} h1 *,
              ${wysiwygScope} h2 *,
              ${wysiwygScope} h3 *,
              ${wysiwygScope} h4 *,
              ${wysiwygScope} h5 *,
              ${wysiwygScope} h6 * {
                color: inherit !important;
              }
              @media (max-width: 768px) {
                ${wysiwygScope} h1 {
                  font-size: 40px !important;
                  line-height: 48px !important;
                }
                ${wysiwygScope} h2 {
                  font-size: 28px !important;
                  line-height: 32px !important;
                }
                ${wysiwygScope} h3 {
                  font-size: 24px !important;
                  line-height: 28px !important;
                }
                ${wysiwygScope} h4 {
                  font-size: 18px !important;
                  line-height: 24px !important;
                }
                ${wysiwygScope} h5 {
                  font-size: 16px !important;
                  line-height: 24px !important;
                }
                ${wysiwygScope} h6 {
                  font-size: 14px !important;
                  line-height: 20px !important;
                }
              }
            `}</style>
            <div
              id={wysiwygScopeId}
              className="cta-section-content prose prose-lg mx-auto max-w-none" // eslint-disable-line tailwindcss/no-custom-classname
              style={{
                fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                fontSize: "20px",
                fontWeight: theme?.fonts?.paragraphRegularWeight || 400,
                lineHeight: "32px",
                color: descriptionColor
              }}
              dangerouslySetInnerHTML={{ __html: stripWysiwygInlineStylesFromHtml(section.body) }}
            />
          </>
        )}

        {/* CTA Button */}
        {showButton && (
          <div className="mt-6">
            <button
              onClick={handleCTAClick}
              className={`inline-flex shrink-0 items-center justify-center gap-3 px-10 py-3 transition-opacity hover:opacity-90 ${getButtonClasses(
                theme?.buttonStyle
              )}`}
              style={applyButtonTheme(
                {
                  backgroundColor: theme?.colors?.secondary || "#E6DAD7",
                  fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                  fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                  color: theme?.colors?.textDark || "#55433F",
                  fontSize: "16px",
                  lineHeight: "24px"
                },
                theme
              )}>
              {buttonText}
            </button>
          </div>
        )}
      </div>
    )
  }

  const renderHighlight = (highlight, index, isHighlightsLayout = false) => {
    // Use theme colors (removed custom color pickers)
    const titleColor = theme?.colors?.textDark || "#000000"
    // For highlights layout, use slightly lighter gray for descriptions to match Figma
    const descriptionColor = isHighlightsLayout
      ? theme?.colors?.textDark || "#666666"
      : theme?.colors?.textDark || "#000000"
    const highlightTitleFontSize = settings.highlightTitleFontSize || "18px"
    const highlightDescriptionFontSize = settings.highlightDescriptionFontSize || "14px"

    // For highlights_2 layout, use centered layout with larger images
    if (isHighlightsLayout) {
      return (
        <div key={index} className="flex flex-col items-center space-y-4 text-center">
          {highlight.imageUrl && (
            <div className="mb-2 w-full">
              <img
                src={highlight.imageUrl}
                alt={highlight.title || `Highlight ${index + 1}`}
                className={twMerge("h-auto w-full object-cover", getThemeShapeClasses(theme))}
                style={{ aspectRatio: "16/9" }}
              />
            </div>
          )}
          {highlight.title && (
            <h3
              className="font-bold"
              style={{
                fontFamily: formatHeadingFontFamily(theme?.fonts?.heading),
                fontWeight: theme?.fonts?.headingWeight || "700",
                color: titleColor,
                fontSize: highlightTitleFontSize,
                lineHeight: "1.2"
              }}>
              {highlight.title}
            </h3>
          )}
          {highlight.description && (
            <>
              <style>{`
              .cta-highlight-content ul {
                list-style-type: disc !important;
                margin-left: 1.5rem !important;
                margin-bottom: 1rem !important;
              }
              .cta-highlight-content ol {
                list-style-type: decimal !important;
                margin-left: 1.5rem !important;
                margin-bottom: 1rem !important;
              }
              .cta-highlight-content li {
                margin-bottom: 0.5rem !important;
                display: list-item !important;
              }
              .cta-highlight-content .ql-align-right {
                text-align: right !important;
              }
              .cta-highlight-content .ql-align-center {
                text-align: center !important;
              }
              .cta-highlight-content .ql-align-justify {
                text-align: justify !important;
              }
              .cta-highlight-content .ql-align-left {
                text-align: left !important;
              }
              .cta-highlight-content a {
                color: ${theme?.colors?.primary || "#BD654F"} !important;
                text-decoration: underline !important;
              }
              .cta-highlight-content a:hover {
                color: ${theme?.colors?.primaryDark || "#A55A45"} !important;
                text-decoration: underline !important;
              }
              .cta-highlight-content p {
                margin-bottom: 0.75rem !important;
                margin-top: 0 !important;
              }
              .cta-highlight-content p:first-child {
                margin-top: 0 !important;
              }
              .cta-highlight-content p:last-child {
                margin-bottom: 0 !important;
              }
              .cta-highlight-content strong,
              .cta-highlight-content b {
                font-weight: ${theme?.fonts?.paragraphBoldWeight || "600"} !important;
              }
              .cta-highlight-content em,
              .cta-highlight-content i {
                font-style: italic !important;
              }
              .cta-highlight-content u {
                text-decoration: underline !important;
              }
              .cta-highlight-content h1, .cta-highlight-content h2, .cta-highlight-content h3,
              .cta-highlight-content h4, .cta-highlight-content h5, .cta-highlight-content h6 {
                font-family: ${formatHeadingFontFamily(theme?.fonts?.heading)} !important;
                font-weight: ${theme?.fonts?.headingWeight || "700"} !important;
                color: ${descriptionColor} !important;
                margin-bottom: 0.5rem !important;
                margin-top: 1rem !important;
              }
              .cta-highlight-content h1 {
                font-size: 40px !important;
                line-height: 48px !important;
              }
              .cta-highlight-content h2 {
                font-size: 28px !important;
                line-height: 32px !important;
              }
              .cta-highlight-content h3 {
                font-size: 24px !important;
                line-height: 28px !important;
              }
              .cta-highlight-content h4 {
                font-size: 18px !important;
                line-height: 24px !important;
              }
              .cta-highlight-content h5 {
                font-size: 16px !important;
                line-height: 24px !important;
              }
              .cta-highlight-content h6 {
                font-size: 14px !important;
                line-height: 20px !important;
              }
            `}</style>
              <div
                className="cta-highlight-content leading-[20px] md:text-base" // eslint-disable-line tailwindcss/no-custom-classname
                style={{
                  fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                  color: descriptionColor,
                  fontSize: highlightDescriptionFontSize,
                  fontWeight: "400"
                }}
                dangerouslySetInnerHTML={{
                  __html: typeof highlight.description === "object" ? highlight.description.html : highlight.description
                }}
              />
            </>
          )}
        </div>
      )
    }

    // Default render for other layouts
    return (
      <div key={index} className="flex flex-col items-center space-y-3 text-center md:space-y-4">
        {highlight.imageUrl && (
          <div className="mb-2 md:mb-4">
            <img
              src={highlight.imageUrl}
              alt={highlight.title || `Highlight ${index + 1}`}
              className={twMerge("size-24 object-cover shadow-lg md:size-32", getThemeShapeClasses(theme))}
            />
          </div>
        )}
        {highlight.title && (
          <h3
            className="font-semibold leading-[24px] md:text-xl"
            style={{
              fontFamily: formatHeadingFontFamily(theme?.fonts?.heading),
              fontWeight: theme?.fonts?.headingWeight || "700",
              color: titleColor,
              fontSize: highlightTitleFontSize
            }}>
            {highlight.title}
          </h3>
        )}
        {highlight.description && (
          <>
            <style>{`
              .cta-highlight-content ul {
                list-style-type: disc !important;
                margin-left: 1.5rem !important;
                margin-bottom: 1rem !important;
              }
              .cta-highlight-content ol {
                list-style-type: decimal !important;
                margin-left: 1.5rem !important;
                margin-bottom: 1rem !important;
              }
              .cta-highlight-content li {
                margin-bottom: 0.5rem !important;
                display: list-item !important;
              }
              .cta-highlight-content .ql-align-right {
                text-align: right !important;
              }
              .cta-highlight-content .ql-align-center {
                text-align: center !important;
              }
              .cta-highlight-content .ql-align-justify {
                text-align: justify !important;
              }
              .cta-highlight-content .ql-align-left {
                text-align: left !important;
              }
              .cta-highlight-content a {
                color: ${theme?.colors?.primary || "#BD654F"} !important;
                text-decoration: underline !important;
              }
              .cta-highlight-content a:hover {
                color: ${theme?.colors?.primaryDark || "#A55A45"} !important;
                text-decoration: underline !important;
              }
              .cta-highlight-content p {
                margin-bottom: 0.75rem !important;
                margin-top: 0 !important;
              }
              .cta-highlight-content p:first-child {
                margin-top: 0 !important;
              }
              .cta-highlight-content p:last-child {
                margin-bottom: 0 !important;
              }
              .cta-highlight-content strong,
              .cta-highlight-content b {
                font-weight: ${theme?.fonts?.paragraphBoldWeight || "600"} !important;
              }
              .cta-highlight-content em,
              .cta-highlight-content i {
                font-style: italic !important;
              }
              .cta-highlight-content u {
                text-decoration: underline !important;
              }
              .cta-highlight-content h1, .cta-highlight-content h2, .cta-highlight-content h3,
              .cta-highlight-content h4, .cta-highlight-content h5, .cta-highlight-content h6 {
                font-family: ${formatHeadingFontFamily(theme?.fonts?.heading)} !important;
                font-weight: ${theme?.fonts?.headingWeight || "700"} !important;
                color: ${descriptionColor} !important;
                margin-bottom: 0.5rem !important;
                margin-top: 1rem !important;
              }
              .cta-highlight-content h1 {
                font-size: 40px !important;
                line-height: 48px !important;
              }
              .cta-highlight-content h2 {
                font-size: 28px !important;
                line-height: 32px !important;
              }
              .cta-highlight-content h3 {
                font-size: 24px !important;
                line-height: 28px !important;
              }
              .cta-highlight-content h4 {
                font-size: 18px !important;
                line-height: 24px !important;
              }
              .cta-highlight-content h5 {
                font-size: 16px !important;
                line-height: 24px !important;
              }
              .cta-highlight-content h6 {
                font-size: 14px !important;
                line-height: 20px !important;
              }
            `}</style>
            <div
              className="cta-highlight-content leading-[20px] md:text-base" // eslint-disable-line tailwindcss/no-custom-classname
              style={{
                fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                color: descriptionColor,
                fontSize: highlightDescriptionFontSize
              }}
              dangerouslySetInnerHTML={{
                __html: typeof highlight.description === "object" ? highlight.description.html : highlight.description
              }}
            />
          </>
        )}
      </div>
    )
  }

  // Layout-specific rendering
  switch (layout) {
    case "content_centered":
      return (
        <section
          className={twMerge("relative py-12 md:py-20", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
          style={{ backgroundColor }}>
          <div className="mx-auto w-[70%] max-w-[70%] px-6">
            <ScrollReveal variant="fadeUp" threshold={0}>
              {section.imageUrl && (
                <div className="mb-8 flex justify-center">
                  <div className="relative">
                    <img
                      src={section.imageUrl}
                      alt={section.headline || "CTA image"}
                      className={twMerge("relative size-64 object-cover", getThemeShapeClasses(theme))}
                    />
                  </div>
                </div>
              )}
              {renderContent("left")}
            </ScrollReveal>
          </div>
        </section>
      )

    case "text_centered":
      return (
        <section
          className={twMerge("relative py-12 md:py-20", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
          style={{ backgroundColor }}>
          <div className="mx-auto w-[70%] max-w-[70%] px-6">
            <ScrollReveal variant="fadeUp" threshold={0}>
              {renderContent("center")}
            </ScrollReveal>
          </div>
        </section>
      )

    case "image_right":
      return (
        <section
          className={twMerge("relative py-12 md:py-20", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
          style={{ backgroundColor }}>
          <div className="mx-auto max-w-6xl px-6">
            <div className="flex flex-row items-center">
              <ScrollReveal variant="fadeUp" threshold={0} className="flex-1 pr-12">
                {renderContent("left")}
              </ScrollReveal>
              {section.imageUrl && (
                <div className="relative flex-1">
                  <img
                    src={section.imageUrl}
                    alt={section.headline || "CTA image"}
                    className={twMerge("h-auto w-full shadow-xl", getThemeShapeClasses(theme))}
                  />
                </div>
              )}
            </div>
          </div>
        </section>
      )

    case "image_left":
      return (
        <section
          className={twMerge("relative py-12 md:py-20", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
          style={{ backgroundColor }}>
          <div className="mx-auto max-w-6xl px-6">
            <div className="flex flex-row items-center">
              {section.imageUrl && (
                <div className="relative flex-1">
                  <img
                    src={section.imageUrl}
                    alt={section.headline || "CTA image"}
                    className={twMerge("h-auto w-full shadow-xl", getThemeShapeClasses(theme))}
                  />
                </div>
              )}
              <ScrollReveal variant="fadeUp" threshold={0} className="flex-1 pl-12">
                {renderContent("left")}
              </ScrollReveal>
            </div>
          </div>
        </section>
      )

    case "half_image_right":
      return (
        <section
          className={twMerge("relative py-24", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
          style={{ backgroundColor }}>
          <div className="mx-auto min-h-[624px] max-w-6xl px-4">
            <div className="relative flex flex-row items-start">
              {/* Text Card - allow it to grow with content (no fixed max height) */}
              <div
                className="relative z-10 shrink-0"
                style={{
                  paddingTop: "10rem",
                  marginRight: "-15rem"
                }}>
                <ScrollReveal
                  variant="fadeUp"
                  threshold={0}
                  className={twMerge("relative", getThemeShapeClasses(theme))}
                  style={{
                    backgroundColor: theme?.colors?.bgLight || "#FFFFFF",
                    width: "680px"
                  }}>
                  <div className="max-w-2xl p-8">{renderContent("left")}</div>
                </ScrollReveal>
              </div>
              {section.imageUrl && (
                <div className="relative shrink-0" style={{ width: "680px", height: "450px" }}>
                  <img
                    src={section.imageUrl}
                    alt={section.headline || "CTA image"}
                    className={twMerge("size-full object-cover shadow-xl", getThemeShapeClasses(theme))}
                    style={{
                      width: "680px",
                      height: "450px",
                      objectFit: "cover"
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </section>
      )

    case "half_image_left":
      return (
        <section
          className={twMerge("relative py-24", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
          style={{ backgroundColor }}>
          <div className="mx-auto min-h-[624px] max-w-6xl px-4">
            <div className="relative flex flex-row items-start">
              {section.imageUrl && (
                <div className="relative shrink-0" style={{ width: "680px", height: "450px" }}>
                  <img
                    src={section.imageUrl}
                    alt={section.headline || "CTA image"}
                    className={twMerge("size-full object-cover shadow-xl", getThemeShapeClasses(theme))}
                    style={{
                      width: "680px",
                      height: "450px",
                      objectFit: "cover"
                    }}
                  />
                </div>
              )}
              {/* Text Card - allow it to grow with content (no fixed max height) */}
              <div
                className="relative z-10 shrink-0"
                style={{
                  paddingTop: "10rem",
                  marginLeft: "-15rem"
                }}>
                <ScrollReveal
                  variant="fadeUp"
                  threshold={0}
                  className={twMerge("relative", getThemeShapeClasses(theme))}
                  style={{
                    backgroundColor: theme?.colors?.bgLight || "#FFFFFF",
                    width: "680px"
                  }}>
                  <div className="max-w-2xl p-8">{renderContent("left")}</div>
                </ScrollReveal>
              </div>
            </div>
          </div>
        </section>
      )

    case "highlights_2":
      return (
        <section
          className={twMerge("relative py-12 md:py-20", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
          style={{ backgroundColor }}>
          <div className="mx-auto max-w-6xl px-6">
            <ScrollReveal variant="fadeUp" threshold={0} className="mb-8 md:mb-12">
              {renderContent("center")}
            </ScrollReveal>
            <div className="grid w-full grid-cols-2 gap-8">
              {highlights.slice(0, 2).map((highlight, index) => renderHighlight(highlight, index, true))}
            </div>
          </div>
        </section>
      )

    case "highlights_3":
      return (
        <section
          className={twMerge("relative py-12 md:py-20", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
          style={{ backgroundColor }}>
          <div className="mx-auto max-w-3xl px-6">
            <ScrollReveal variant="fadeUp" threshold={0} className="mb-8 md:mb-12">
              {renderContent("center")}
            </ScrollReveal>
            <div className="grid w-full grid-cols-3 gap-6 lg:gap-8">
              {highlights.slice(0, 3).map((highlight, index) => renderHighlight(highlight, index))}
            </div>
          </div>
        </section>
      )

    case "bento_left": {
      const images = settings.images || []
      return (
        <section
          className={twMerge("relative py-12 md:py-20", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
          style={{ backgroundColor }}>
          <div className="mx-auto max-w-6xl px-6">
            <div className="flex flex-row items-start gap-8">
              {/* Images Grid - Left Side */}
              <div className="flex-1">
                <div className="grid grid-cols-2 gap-4">
                  {images.slice(0, 3).map(
                    (imageUrl, index) =>
                      imageUrl && (
                        <div key={index} className={index === 0 ? "col-span-2" : ""}>
                          <img
                            src={imageUrl}
                            alt={`Bento image ${index + 1}`}
                            className={twMerge("h-auto w-full object-cover shadow-lg", getThemeShapeClasses(theme))}
                          />
                        </div>
                      )
                  )}
                </div>
              </div>
              {/* Content - Right Side */}
              <ScrollReveal variant="fadeUp" threshold={0} className="flex-1">
                {renderContent("left")}
              </ScrollReveal>
            </div>
          </div>
        </section>
      )
    }

    case "overlap_double_image": {
      const images = settings.images || []
      return (
        <section
          className={twMerge("relative py-12 md:py-24", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
          style={{ backgroundColor }}>
          <div className="mx-auto max-w-6xl px-6">
            <ScrollReveal variant="fadeUp" threshold={0} className="mb-12 text-center">
              {renderContent("center")}
            </ScrollReveal>
            {/* Overlapping Images */}
            <div className="relative mx-auto max-w-4xl" style={{ minHeight: "450px" }}>
              {images[0] && (
                <div className="absolute left-0 top-0 z-10 w-1/2">
                  <img
                    src={images[0]}
                    alt="Overlap image 1"
                    className={twMerge("h-auto w-full object-cover shadow-2xl", getThemeShapeClasses(theme))}
                    style={{ maxHeight: "400px" }}
                  />
                </div>
              )}
              {images[1] && (
                <div className="absolute bottom-0 right-0 w-1/2">
                  <img
                    src={images[1]}
                    alt="Overlap image 2"
                    className={twMerge("h-auto w-full object-cover shadow-2xl", getThemeShapeClasses(theme))}
                    style={{ maxHeight: "400px" }}
                  />
                </div>
              )}
            </div>
          </div>
        </section>
      )
    }

    default:
      return (
        <section
          className={twMerge("relative py-12 md:py-20", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
          style={{ backgroundColor }}>
          <div className="mx-auto max-w-3xl px-6">
            <ScrollReveal variant="fadeUp" threshold={0}>
              {renderContent("center")}
            </ScrollReveal>
          </div>
        </section>
      )
  }
}

export default CTASectionDesktop
